/*
 * Copyright © 2015-2018 Serenova, LLC. All rights reserved.
 */

/**
 *
 * SkillsDetailsPanel
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { SidePanelTable, DetailHeader, Toast } from 'cx-ui-components';
import DetailWrapper from '../../../components/DetailWrapper';
import { detailHeaderText } from '../../../utils';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const WrappedDetailHeader = styled(DetailHeader)`
  margin-left: 35px;
`;

const ProficiencyCellInput = styled.input`
  max-width: 80px;
  padding-left: 8px;
`;

export default function SkillsDetailsPanel({
  children,
  pendingProficiencies,
  setPendingProficiency,
  updateProficiency,
  userHasUpdatePermission,
  userCanRemoveProficiency,
  userCanManageProficiency,
  hasProficiency,
  usersItems,
  usersFields,
  usersFetching,
  outboundIdentifierListsItems,
  outboundIdentifierListsFields,
  outboundIdentifierListsFetching,
  removeListItem,
  setSelectedSubEntityId,
  defaultFilters,
  sidePanelReadPermissions,
  sidePanelUpdatePermissions,
  itemApiPending,
}) {
  const ProficiencyCell = ({ id, value, onChange }) => (
    <ProficiencyCellInput
      type="number"
      title={'Proficiency'}
      value={pendingProficiencies.get(id) || value}
      onChange={onChange}
    />
  );

  if (hasProficiency) {
    usersFields = [
      ...usersFields,
      {
        label: 'Proficiency',
        name: 'proficiency',
        isColumnEditable: userCanManageProficiency,
        filterable: false,
        actions: {
          onChange: (id, proficiencyValue) => {
            if (proficiencyValue >= 1 && proficiencyValue <= 100) {
              setPendingProficiency(id, proficiencyValue);
            } else {
              return Toast.error('Skill level must be a number between 0 and 100');
            }
          },
        },
        cellOverride: ProficiencyCell,
      },
    ];
  }

  return (
    <Wrapper id="dtpanel-skills">
      {children}

      {sidePanelReadPermissions.users && (
        <DetailWrapper open={true} data-automation="usersSVG" contains="users" autoCloseOverride>
          <WrappedDetailHeader
            userHasUpdatePermission={!usersFetching && userHasUpdatePermission && sidePanelUpdatePermissions.users}
            text={`${usersItems.length > 1 ? usersItems.length : ''} Users`}
            onActionButtonClick={() => setSelectedSubEntityId('users')}
          />
          <SidePanelTable
            tableType={'sidePanel'}
            contains="users"
            userHasUpdatePermission={userHasUpdatePermission && sidePanelUpdatePermissions.users}
            updateSubEntity={(listItemId) => updateProficiency(listItemId)}
            shouldShowDeleteButtonOnItem={() => userCanRemoveProficiency}
            shouldShowUpdateButtonOnItem={(user) => hasProficiency && !!pendingProficiencies.get(user.id)}
            deleteSubEntity={removeListItem}
            items={usersItems}
            fields={usersFields}
            filtered={defaultFilters.users}
            fetching={usersFetching}
            itemApiPending={itemApiPending}
          />
        </DetailWrapper>
      )}
      {sidePanelReadPermissions.outboundIdentifierLists && (
        <DetailWrapper open={false} data-automation="outboundIdentifierListsSVG" contains="outboundIdentifierLists">
          <WrappedDetailHeader
            userHasUpdatePermission={
              !outboundIdentifierListsFetching &&
              userHasUpdatePermission &&
              sidePanelUpdatePermissions.outboundIdentifierLists
            }
            text={detailHeaderText(outboundIdentifierListsItems, 'Outbound Identifier Lists')}
            onActionButtonClick={() => setSelectedSubEntityId('outboundIdentifierLists')}
          />
          <SidePanelTable
            tableType={'sidePanel'}
            contains="outboundIdentifierLists"
            userHasUpdatePermission={userHasUpdatePermission && sidePanelUpdatePermissions.outboundIdentifierLists}
            deleteSubEntity={removeListItem}
            items={outboundIdentifierListsItems}
            fields={outboundIdentifierListsFields}
            filtered={defaultFilters.outboundIdentifierLists}
            fetching={outboundIdentifierListsFetching}
            itemApiPending={itemApiPending}
          />
        </DetailWrapper>
      )}
    </Wrapper>
  );
}

SkillsDetailsPanel.propTypes = {
  updateProficiency: PropTypes.func,
  selectedEntityId: PropTypes.string,
  userHasUpdatePermission: PropTypes.bool,
  userCanManageProficiency: PropTypes.bool,
  userCanRemoveProficiency: PropTypes.bool,
  children: PropTypes.any,
  hasProficiency: PropTypes.bool,
  usersItems: PropTypes.array,
  usersFields: PropTypes.array,
  usersFetching: PropTypes.bool,
  outboundIdentifierListsItems: PropTypes.array,
  outboundIdentifierListsFields: PropTypes.array,
  outboundIdentifierListsFetching: PropTypes.bool,
  removeListItem: PropTypes.func,
  setSelectedSubEntityId: PropTypes.func,
  defaultFilters: PropTypes.object,
  sidePanelReadPermissions: PropTypes.object,
  sidePanelUpdatePermissions: PropTypes.object,
  itemApiPending: PropTypes.string,
};
